import { useState } from 'react';
import { Check, Loader2, Pencil } from 'lucide-react';

import { useIdeaCatalogue, useSetIdeaIndustry } from '@/api/queries';
import type { IdeaDetail } from '@/api/types';
import { FieldError, fieldA11y } from '@/ui/FieldError';

import { IndustryLabel } from '../ideaUi';
import { IndustryOptions } from './IndustryOptions';

type SetIndustry = ReturnType<typeof useSetIdeaIndustry>;

interface EditorProps {
  idea: IdeaDetail;
  draft: string;
  setDraft: (draft: string) => void;
  setIndustry: SetIndustry;
  onEditing: (editing: boolean) => void;
}

function IndustryEditor({ idea, draft, setDraft, setIndustry, onEditing }: EditorProps) {
  const catalogue = useIdeaCatalogue();
  const current = idea.industry;
  const pending = setIndustry.isPending;
  const cancel = () => {
    setIndustry.reset();
    onEditing(false);
  };
  const save = () => setIndustry.mutate({ id: idea.id, industry_id: draft || null }, { onSuccess: () => onEditing(false) });
  return (
    <span className="industry-control">
      <select
        id="idea-industry-edit"
        className="toolbar-select"
        aria-label="Industry"
        {...fieldA11y('idea-industry-edit', setIndustry.error?.message)}
        value={draft}
        disabled={pending}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => e.key === 'Escape' && cancel()}
      >
        <IndustryOptions current={current} industries={catalogue.data?.industries} />
      </select>
      <button type="button" className="btn btn-primary btn-sm" disabled={pending || draft === (current?.id ?? '')} onClick={save}>
        {pending ? <Loader2 aria-hidden="true" className="spin" /> : <Check aria-hidden="true" />} Save
      </button>
      <button type="button" className="btn btn-secondary btn-sm" disabled={pending} onClick={cancel}>
        Cancel
      </button>
      <FieldError inputId="idea-industry-edit" message={setIndustry.error?.message} />
    </span>
  );
}

/**
 * The idea's industry, read-only until someone who may change it presses the
 * pencil (or Change industry in the actions menu): then a select with Save
 * and Cancel. Nothing is saved until Save.
 */
/** The contract types `industry_fit` as a free object: its rationale, when it carries one as text. */
function fitRationale(fit: Record<string, unknown> | null | undefined): string | undefined {
  return typeof fit?.rationale === 'string' ? fit.rationale : undefined;
}

export function IndustryControl({ idea, editing, onEditing }: { idea: IdeaDetail; editing: boolean; onEditing: (editing: boolean) => void }) {
  const setIndustry = useSetIdeaIndustry();
  const [draft, setDraft] = useState(idea.industry?.id ?? '');
  const canChange = idea.permissions.can_change_industry;

  if (editing && canChange) {
    return <IndustryEditor idea={idea} draft={draft} setDraft={setDraft} setIndustry={setIndustry} onEditing={onEditing} />;
  }
  const startEditing = () => {
    setDraft(idea.industry?.id ?? '');
    setIndustry.reset();
    onEditing(true);
  };
  return (
    <span className="industry-control">
      <IndustryLabel idea={idea} rationale={fitRationale(idea.current_research?.industry_fit)} />
      {canChange && (
        <button
          type="button"
          className="btn btn-ghost btn-icon btn-sm industry-edit"
          aria-label="Change the industry"
          title="Change the industry"
          onClick={startEditing}
        >
          <Pencil aria-hidden="true" />
        </button>
      )}
    </span>
  );
}
